// src/components/admin/AdminLayout.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  FiHome,
  FiPackage,
  FiShoppingBag,
  FiUsers,
  FiTag,
  FiBarChart2,
  FiMessageSquare,
  FiDollarSign,
  FiTrendingUp,
  FiLogOut,
  FiBell,
  FiMenu,
  FiX,
  FiMapPin
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  let user = null;
  try {
    const raw = localStorage.getItem('user');
    user = raw ? JSON.parse(raw) : null;
  } catch (e) {
    user = null;
  }

  const menuItems = [
    { path: '/admin', icon: FiHome, label: 'Tableau de bord', exact: true },
    { path: '/admin/deals', icon: FiPackage, label: 'Gestion des Deals' },
    { path: '/admin/orders', icon: FiShoppingBag, label: 'Commandes' },
    { path: '/admin/users', icon: FiUsers, label: 'Utilisateurs' },
    { path: '/admin/merchants', icon: FiTag, label: 'Marchands' },
    { path: '/admin/categories', icon: FiTag, label: 'Catégories' },

    // ✅ ADDED: Cities
    { path: '/admin/cities', icon: FiMapPin, label: 'Villes' },

    { path: '/admin/performance', icon: FiBarChart2, label: 'Performance' },
    { path: '/admin/reviews', icon: FiMessageSquare, label: 'Avis' },
    { path: '/admin/payments', icon: FiDollarSign, label: 'Paiements' },
    { path: '/admin/analytics', icon: FiTrendingUp, label: 'Analyses' }
  ];

  useEffect(() => {
    mountedRef.current = true;
    fetchUnreadCount();

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const endpoints = [
        '/admin/notifications/unread-count',
        '/notifications/unread-count',
        '/admin/notifications/count'
      ];

      let response = null;
      for (const ep of endpoints) {
        try {
          response = await API.get(ep);
          if (response?.data) break;
        } catch (err) {}
      }

      if (!response || !response.data) {
        if (!mountedRef.current) return;
        setUnreadCount(0);
        return;
      }

      const data = response.data;
      if (!mountedRef.current) return;
      setUnreadCount(Number(data.count || 0));
    } catch (error) {
      if (!mountedRef.current) return;
      setUnreadCount(0);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const formatBadge = (count) => {
    if (!count || count <= 0) return null;
    if (count > 99) return '99+';
    if (count > 9) return '9+';
    return String(count);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="fixed top-0 z-30 w-full bg-white border-b border-gray-200">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="inline-flex items-center p-2 text-gray-600 rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none"
                aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              >
                {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
              <Link to="/admin" className="flex ml-2 md:mr-24">
                <span className="self-center text-xl font-bold text-primary-600 whitespace-nowrap">
                  MAXULA
                </span>
                <span className="ml-2 text-sm font-normal text-gray-500 self-center">
                  Admin
                </span>
              </Link>
            </div>

            <div className="flex items-center space-x-3">
              <Link
                to="/admin/notifications"
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Notifications"
              >
                <FiBell size={20} />
                {formatBadge(unreadCount) && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                    {formatBadge(unreadCount)}
                  </span>
                )}
              </Link>

              <div className="flex items-center space-x-3 pl-4 border-l">
                <img
                  src={
                    user?.avatar ||
                    'https://ui-avatars.com/api/?name=Admin&background=10b981&color=fff'
                  }
                  alt={
                    user?.firstName
                      ? `${user.firstName} ${user.lastName}`
                      : 'Admin'
                  }
                  className="w-8 h-8 rounded-full"
                />
                <div className="hidden md:block">
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">Administrateur</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <aside
        className={`fixed inset-y-0 left-0 z-20 w-64 bg-white border-r border-gray-200 pt-16 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full overflow-y-auto py-4">
          <nav className="px-3 space-y-1">
            {menuItems.map((item) => {
              const active = isActive(item.path, item.exact);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </Link>
              );
            })}

            <Link
              to="/admin/notifications"
              className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive('/admin/notifications')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <div className="flex items-center">
                <FiBell className="w-5 h-5 mr-3" />
                Notifications
              </div>
              {formatBadge(unreadCount) && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                  {formatBadge(unreadCount)}
                </span>
              )}
            </Link>
          </nav>

          <div className="px-3 mt-6 pt-6 border-t">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <FiLogOut className="w-5 h-5 mr-3" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-10 bg-gray-900 bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="pt-16 lg:pl-64">
        <main className="p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
